import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

/**
 * Custom hook to fetch company team data using optimized RPC
 * Includes server-side pagination, filtering, and sorting
 *
 * @param {string} companyId - The company ID
 * @param {object} options - Configuration options
 * @param {string} options.search - Search query
 * @param {string} options.status - Status filter ('all', 'active', 'invited', 'pending')
 * @param {string} options.accountStatus - Account status filter ('all', 'with_account', 'without_account')
 * @param {string} options.archiveStatus - Archive status filter ('active', 'archived', 'all')
 * @param {string} options.spaceName - Space name filter
 * @param {string} options.sortField - Sort field
 * @param {string} options.sortDirection - Sort direction ('asc', 'desc')
 * @param {number} options.page - Current page
 * @param {number} options.limit - Items per page
 * @param {boolean} options.enabled - Whether to fetch data
 * @returns {object} { members, spaces, stats, pagination, loading, error, refetch }
 */
export const useCompanyTeamData = (companyId, options = {}) => {
  const {
    search = '',
    status = 'all',
    accountStatus = 'all',
    archiveStatus = 'active',
    spaceName = 'all',
    sortField = 'name',
    sortDirection = 'asc',
    page = 1,
    limit = 10,
    enabled = true
  } = options;

  const [members, setMembers] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, spaces: 0, admins: 0, pending: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalCount: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const debounceTimerRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const fetchData = useCallback(async () => {
    if (!companyId || !enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.rpc('get_company_team_data', {
        p_company_id: companyId,
        p_user_id: null, // Not needed for this RPC
        p_search: debouncedSearch || null,
        p_status: status,
        p_space_name: spaceName,
        p_account_status: accountStatus,
        p_archive_status: archiveStatus,
        p_sort_field: sortField,
        p_sort_direction: sortDirection,
        p_page: page,
        p_limit: limit
      });

      if (fetchError) throw fetchError;

      // Combine members and invitations
      const allMembers = [
        ...(data.members || []),
        ...(data.invitations || [])
      ];

      setMembers(allMembers);
      setSpaces(data.spaces || []);
      setStats(data.stats || { total: 0, active: 0, spaces: 0, admins: 0, pending: 0 });
      setPagination(data.pagination || { page: 1, limit: 10, totalCount: 0, totalPages: 0 });
    } catch (err) {
      console.error('Error fetching company team data:', err);
      setError(err);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [companyId, debouncedSearch, status, accountStatus, archiveStatus, spaceName, sortField, sortDirection, page, limit, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get unique space names for filter dropdown
  const uniqueSpaceNames = spaces.map(s => s.name);

  return {
    members,
    spaces,
    uniqueSpaceNames,
    stats,
    pagination,
    loading,
    error,
    refetch: fetchData
  };
};
